/**
 * BeforeAfterEditor — Edit before/after cases with bulk image upload.
 * List: image, title, order (editable), delete (DELETE .../before-after/:index).
 * Bulk: drag-drop or multiple file input → POST .../before-after with files[].
 * React + Reactstrap, no Next.js.
 */

import React, { useCallback, useMemo, useState } from "react";
import { Button, FormGroup, Input } from "reactstrap";
import { Icon } from "../../Component";
import http from "../../../utils/http-common";

const DEFAULT_IMAGE_PLACEHOLDER = "https://via.placeholder.com/120?text=Before%2FAfter";
const MAX_TITLE_LENGTH = 200;
const MAX_ORDER = 9999;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_FILES_BULK = 20;
const MAX_FILE_SIZE_MB = 10;

const getBaseUrl = () => process.env.REACT_APP_API_URL || "";

const BeforeAfterEditor = ({
  cases = [],
  onChange,
  getImageUrl,
  disabled = false,
  clinicId,
  onRefresh,
}) => {
  const safeCases = useMemo(() => (Array.isArray(cases) ? cases : []), [cases]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const resolvePreviewUrl = useCallback(
    (imageUrl) => {
      if (!imageUrl || !String(imageUrl).trim()) return DEFAULT_IMAGE_PLACEHOLDER;
      if (getImageUrl && typeof getImageUrl === "function") {
        return getImageUrl(imageUrl) || imageUrl;
      }
      if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
        return imageUrl;
      }
      const base = getBaseUrl();
      return imageUrl.startsWith("/") ? `${base}${imageUrl}` : `${base}/${imageUrl}`;
    },
    [getImageUrl]
  );

  const handleAdd = useCallback(() => {
    if (disabled) return;
    const nextOrder =
      safeCases.length > 0
        ? Math.max(...safeCases.map((c) => Number(c.order) ?? 0)) + 1
        : 0;
    onChange([
      ...safeCases,
      { title: `Case ${safeCases.length + 1}`, imageUrl: "", order: nextOrder },
    ]);
  }, [safeCases, onChange, disabled]);

  const handleRemove = useCallback(
    async (index) => {
      if (disabled) return;
      if (!clinicId) {
        const next = safeCases.filter((_, i) => i !== index);
        onChange(next.map((item, i) => ({ ...item, order: i })));
        return;
      }
      try {
        await http.delete(
          `/api/v1/partners/medical/clinics/${clinicId}/before-after/${index}`
        );
        if (onRefresh) onRefresh();
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Delete failed";
        if (typeof window !== "undefined" && window.swal) {
          window.swal("Error", msg, "error");
        }
      }
    },
    [disabled, clinicId, safeCases, onChange, onRefresh]
  );

  const handleChange = useCallback(
    (index, field, value) => {
      if (disabled) return;
      const next = safeCases.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      );
      if (field === "order") {
        const num = parseInt(value, 10);
        if (!Number.isNaN(num) && num >= 0 && num <= MAX_ORDER) {
          next[index].order = num;
        }
      }
      if (field === "title") {
        next[index].title = String(value).slice(0, MAX_TITLE_LENGTH);
      }
      onChange(next);
    },
    [safeCases, onChange, disabled]
  );

  const validateFiles = useCallback((files) => {
    const list = Array.from(files).filter((f) => f && f.type && ALLOWED_IMAGE_TYPES.includes(f.type));
    const tooBig = list.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (tooBig.length > 0) {
      return { valid: [], error: `Max ${MAX_FILE_SIZE_MB}MB per file. ${tooBig.length} file(s) skipped.` };
    }
    if (list.length > MAX_FILES_BULK) {
      return {
        valid: list.slice(0, MAX_FILES_BULK),
        error: `Max ${MAX_FILES_BULK} files per upload. Only first ${MAX_FILES_BULK} used.`,
      };
    }
    return { valid: list, error: null };
  }, []);

  const doBulkUpload = useCallback(
    async (fileList) => {
      if (!clinicId || fileList.length === 0) return;
      setUploadError(null);
      setUploading(true);
      try {
        const formData = new FormData();
        fileList.forEach((file) => formData.append("files", file));
        await http.post(
          `/api/v1/partners/medical/clinics/${clinicId}/before-after`,
          formData
        );
        if (onRefresh) onRefresh();
        if (typeof window !== "undefined" && window.swal) {
          window.swal("Success", `${fileList.length} image(s) uploaded.`, "success");
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || "Upload failed";
        setUploadError(msg);
        if (typeof window !== "undefined" && window.swal) {
          window.swal("Error", msg, "error");
        }
      } finally {
        setUploading(false);
      }
    },
    [clinicId, onRefresh]
  );

  const onFileInputChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      const { valid, error } = validateFiles(files);
      if (error && typeof window !== "undefined" && window.swal) {
        window.swal("Warning", error, "warning");
      }
      if (valid.length > 0) doBulkUpload(valid);
      e.target.value = "";
    },
    [validateFiles, doBulkUpload]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      const { valid, error } = validateFiles(files);
      if (error && typeof window !== "undefined" && window.swal) {
        window.swal("Warning", error, "warning");
      }
      if (valid.length > 0) doBulkUpload(valid);
    },
    [validateFiles, doBulkUpload]
  );

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const sortedCases = [...safeCases].sort(
    (a, b) => (Number(a.order) ?? 0) - (Number(b.order) ?? 0)
  );

  return (
    <div className="before-after-editor" role="group" aria-label="Before/After cases">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="overline-title">Before/After cases</span>
        {!disabled && (
          <Button
            color="light"
            size="sm"
            onClick={handleAdd}
            type="button"
            aria-label="Add before/after case"
          >
            <Icon name="plus" className="me-1" />
            Add case
          </Button>
        )}
      </div>
      <p className="text-soft small mb-3">
        Used in the &quot;Real results&quot; section on the clinic landing. Order determines display order.
      </p>

      {/* Bulk upload: drag-drop or multiple files */}
      {!disabled && clinicId && (
        <FormGroup className="mb-3">
          <div
            role="button"
            tabIndex={0}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") e.preventDefault();
            }}
            style={{
              border: `2px dashed ${dragOver ? "#0d6efd" : "#dee2e6"}`,
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              background: dragOver ? "#f8f9fa" : "#fff",
              cursor: uploading ? "wait" : "pointer",
            }}
          >
            <input
              type="file"
              accept={ALLOWED_IMAGE_TYPES.join(",")}
              multiple
              onChange={onFileInputChange}
              disabled={uploading}
              style={{ display: "none" }}
              id="before-after-bulk-upload"
            />
            <label
              htmlFor="before-after-bulk-upload"
              style={{ cursor: uploading ? "wait" : "pointer", marginBottom: 0 }}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Uploading…
                </>
              ) : (
                <>
                  <Icon name="upload" className="me-2" />
                  Drag and drop multiple images here, or click to select (max {MAX_FILES_BULK}, {MAX_FILE_SIZE_MB}MB each)
                </>
              )}
            </label>
          </div>
          {uploadError && (
            <p className="text-danger small mt-2 mb-0">{uploadError}</p>
          )}
        </FormGroup>
      )}

      {safeCases.length === 0 ? (
        <p className="text-soft small">No cases yet. Add cases or upload images above.</p>
      ) : (
        <div className="list-group list-group-flush">
          {sortedCases.map((item, sortedIndex) => {
            const index = safeCases.indexOf(item);
            return (
              <div
                key={`${index}-${item.title}-${item.order}`}
                className="list-group-item d-flex flex-column flex-md-row align-items-start align-items-md-center flex-g-3 py-3"
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  marginBottom: "8px",
                }}
              >
                <div className="flex-shrink-0" style={{ width: "80px", height: "80px" }}>
                  <img
                    src={resolvePreviewUrl(item.imageUrl)}
                    alt={item.title || `Case ${sortedIndex + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: "1px solid #e5e7eb",
                    }}
                    onError={(e) => {
                      e.target.src = DEFAULT_IMAGE_PLACEHOLDER;
                    }}
                  />
                </div>
                <div className="flex-grow-1 min-w-0">
                  <FormGroup className="mb-2 mb-md-0">
                    <label className="form-label small mb-0">Title</label>
                    <Input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="e.g. Case 1"
                      value={item.title || ""}
                      onChange={(e) => handleChange(index, "title", e.target.value)}
                      disabled={disabled}
                      maxLength={MAX_TITLE_LENGTH}
                      aria-label={`Case ${sortedIndex + 1} title`}
                    />
                  </FormGroup>
                  <FormGroup className="mb-2 mb-md-0">
                    <label className="form-label small mb-0">Image URL</label>
                    <Input
                      type="url"
                      className="form-control form-control-sm"
                      placeholder="Paste image URL or leave empty and upload above"
                      value={item.imageUrl || ""}
                      onChange={(e) => handleChange(index, "imageUrl", e.target.value.trim())}
                      disabled={disabled}
                      aria-label={`Case ${sortedIndex + 1} image URL`}
                    />
                  </FormGroup>
                  <FormGroup className="mb-0">
                    <label className="form-label small mb-0">Order</label>
                    <Input
                      type="number"
                      min={0}
                      max={MAX_ORDER}
                      className="form-control form-control-sm"
                      value={item.order != null ? item.order : index}
                      onChange={(e) => handleChange(index, "order", e.target.value)}
                      disabled={disabled}
                      aria-label={`Case ${sortedIndex + 1} order`}
                    />
                  </FormGroup>
                </div>
                {!disabled && (
                  <div className="flex-shrink-0">
                    <Button
                      color="danger"
                      size="sm"
                      outline
                      type="button"
                      onClick={() => handleRemove(index)}
                      aria-label={`Remove case ${sortedIndex + 1}`}
                    >
                      <Icon name="trash" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BeforeAfterEditor;
